// ============================================================
// Persistent Cache — IndexedDB-backed with in-memory L1 layer
// ============================================================
// Two-tier caching:
//   L1: in-memory Map (sub-millisecond reads, lost on reload)
//   L2: IndexedDB (persists across reloads, survives PWA cold starts)
//
// On cold start: L2 hit → show data instantly, refresh in background
// On navigation: L1 hit → instant, no network at all
// After write: invalidate relevant keys in both tiers
// ============================================================

const DB_NAME    = 'myfinance_cache';
const DB_VERSION = 1;
const STORE_NAME = 'cache';
const L1 = new Map();  // in-memory layer

// Default TTLs (milliseconds)
const TTL = {
  balances:     5  * 60 * 1000,   // 5 min  — balances change on every transaction
  transactions: 2  * 60 * 1000,   // 2 min  — transaction lists
  dashboard:    2  * 60 * 1000,   // 2 min
  settings:     30 * 60 * 1000,   // 30 min — settings rarely change
  rates:        60 * 60 * 1000,   // 1 hour — exchange rates
  default:      5  * 60 * 1000,
};

function getTTL(key) {
  if (key.startsWith('balance'))      return TTL.balances;
  if (key.startsWith('tx:'))          return TTL.transactions;
  if (key.startsWith('dashboard'))    return TTL.dashboard;
  if (key.startsWith('settings'))     return TTL.settings;
  if (key.startsWith('rates'))        return TTL.rates;
  return TTL.default;
}

// ── IndexedDB helpers ─────────────────────────────────────────────────────────
let _db = null;

async function getDB() {
  if (_db) return _db;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess  = e => { _db = e.target.result; resolve(_db); };
    req.onerror    = e => reject(e.target.error);
  });
}

async function idbGet(key) {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror   = () => resolve(null); // fail silently
    });
  } catch { return null; }
}

async function idbSet(key, value) {
  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror    = () => resolve(); // fail silently
    });
  } catch { /* ignore */ }
}

async function idbDeletePrefix(prefix) {
  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const keys = req.result.filter(k => k.startsWith(prefix));
        keys.forEach(k => store.delete(k));
        resolve(keys.length);
      };
      tx.onerror = () => resolve(0);
    });
  } catch { return 0; }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Read from cache (L1 first, then L2).
 * Returns { data, stale } where stale=true means expired but returned anyway.
 * Returns null if not found at all.
 */
export async function cacheGet(key) {
  // L1
  const l1 = L1.get(key);
  if (l1) {
    if (Date.now() - l1.ts < getTTL(key)) return { data: l1.data, stale: false };
    // L1 expired — fall through to check L2 or return stale
    return { data: l1.data, stale: true };
  }
  // L2
  const l2 = await idbGet(key);
  if (!l2) return null;
  const stale = Date.now() - l2.ts > getTTL(key);
  // Populate L1 from L2
  L1.set(key, l2);
  return { data: l2.data, stale };
}

/**
 * Write to both cache tiers.
 */
export async function cacheSet(key, data) {
  const entry = { data, ts: Date.now() };
  L1.set(key, entry);
  await idbSet(key, entry); // async, non-blocking
}

/**
 * Delete a key from both tiers.
 */
export function cacheDelete(key) {
  L1.delete(key);
  idbGet(key).then(() => idbDeletePrefix(key)); // approximate delete
}

/**
 * Invalidate all keys matching a prefix.
 */
export async function cacheInvalidatePrefix(prefix) {
  // L1: iterate and delete matches
  for (const k of L1.keys()) {
    if (k.startsWith(prefix)) L1.delete(k);
  }
  // L2: delete by prefix
  await idbDeletePrefix(prefix);
}

/**
 * Invalidate everything (on logout or major write).
 */
export async function cacheInvalidateAll() {
  L1.clear();
  await idbDeletePrefix(''); // deletes all keys
}

/**
 * Stale-while-revalidate pattern:
 * 1. Return cached data immediately (even if stale)
 * 2. If stale or missing, fetch fresh data in background
 * 3. Call onFresh(freshData) when background fetch completes
 *
 * This is the pattern that makes navigation feel instant.
 */
export async function cacheStaleWhileRevalidate(key, fetchFn, onFresh) {
  const cached = await cacheGet(key);

  if (cached && !cached.stale) {
    // Fresh cache — return immediately, no background fetch
    return cached.data;
  }

  if (cached && cached.stale) {
    // Stale — return immediately AND refresh in background
    fetchFn().then(fresh => {
      cacheSet(key, fresh);
      onFresh?.(fresh);
    }).catch(() => { /* silent */ });
    return cached.data;
  }

  // Cache miss — must wait for fresh data
  const fresh = await fetchFn();
  cacheSet(key, fresh);
  return fresh;
}
